import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, CreditCard, Download, ExternalLink } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { getConciergeDisplayName } from "@/lib/i18n";
import { storePostLoginRedirect } from "@/lib/auth-redirect";
import { getMyConciergeRequest } from "@/lib/concierge.functions";
import {
  conciergeTimelineLocalized,
  derivePaymentState,
  deriveRequestState,
  getPaymentStateLabel,
  getRequestStateLabel,
  showPaymentButton,
  type ConciergeSnapshot,
} from "@/lib/concierge-workflow";

export const Route = createFileRoute("/concierge/$id")({ component: ConciergeDetailPage });

function ConciergeDetailPage() {
  const { id } = Route.useParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { lang } = useLang();
  const conciergeName = getConciergeDisplayName(lang);
  const navigate = useNavigate();
  const getFn = useServerFn(getMyConciergeRequest);
  const [row, setRow] = useState<Awaited<ReturnType<typeof getFn>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      storePostLoginRedirect();
      navigate({ to: "/login" });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setLoading(true);
    getFn({ data: { id } })
      .then((r) => setRow(r))
      .catch((e) => toast.error(e instanceof Error ? e.message : "Erro ao carregar"))
      .finally(() => setLoading(false));
  }, [id, isAuthenticated, getFn]);

  if (authLoading || loading) {
    return (
      <main className="mx-auto flex min-h-[60vh] w-full max-w-3xl items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }
  if (!row) return null;

  const hasContent = Boolean(row.deliverable_text || row.pdfSignedUrl || row.app_route_link);
  const snap: ConciergeSnapshot = {
    status: row.status,
    paymentStatus: row.payment_status ?? "",
    isFirstFree: row.is_first_free,
    priceCents: row.price_cents,
    deliveredAt: row.delivered_at,
    hasDeliverable: hasContent,
    activeQuote: row.active_quote ?? null,
  };
  const requestState = deriveRequestState(snap);
  const paymentState = derivePaymentState(snap);
  const steps = conciergeTimelineLocalized(snap, lang);
  const isDelivered = requestState === "entregue";
  const priceEur = row.price_cents ? (row.price_cents / 100).toFixed(2) : null;

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8">
      <Link to="/concierge" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Os meus pedidos
      </Link>

      <header className="mt-4 mb-6">
        <div className="text-xs font-semibold uppercase tracking-wide text-brand">{conciergeName}</div>
        <h1 className="text-2xl font-bold">{row.destination}</h1>
        <p className="text-sm text-muted-foreground">
          {row.start_date || "—"} → {row.end_date || "—"} · {row.people ?? "—"} pessoa(s)
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <span className="inline-block rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">
            {getRequestStateLabel(requestState, lang)}
          </span>
          {!row.is_first_free && (
            <span className="inline-block rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
              Pagamento: {getPaymentStateLabel(paymentState, lang)}
            </span>
          )}
        </div>
      </header>

      <section className="mb-6 rounded-2xl border border-border bg-card p-5">
        <div className="text-xs font-bold uppercase text-muted-foreground">Progresso do pedido</div>
        <ol className="mt-3 space-y-2">
          {steps.map((s) => (
            <li key={s.id} className="flex items-center gap-2 text-sm">
              <span
                className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                  s.state === "done"
                    ? "bg-emerald-500"
                    : s.state === "current"
                      ? "bg-brand"
                      : "bg-muted-foreground/30"
                }`}
              />
              <span
                className={
                  s.state === "pending"
                    ? "text-muted-foreground"
                    : s.state === "current"
                      ? "font-semibold text-foreground"
                      : "text-foreground"
                }
              >
                {s.label}
              </span>
            </li>
          ))}
        </ol>
      </section>

      {showPaymentButton(snap) && row.checkout_url && (
        <section className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-5">
          <div className="text-sm font-semibold text-amber-900">A tua rota personalizada está pronta!</div>
          <p className="mt-1 text-sm text-amber-800">
            Conclui o pagamento seguro para receberes a rota final.
          </p>
          <a href={row.checkout_url}
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand/90">
            <CreditCard className="h-4 w-4" /> Pagar {priceEur ? `€ ${priceEur}` : ""}
          </a>
        </section>
      )}

      {paymentState === "expirado" && !isDelivered && (
        <section className="mb-6 rounded-2xl border border-orange-300 bg-orange-50 p-5 text-sm text-orange-900">
          O link de pagamento expirou. A equipa Routify vai enviar-te um novo link — não é preciso fazeres nada.
        </section>
      )}


      {!isDelivered && requestState !== "cancelado" && (
        <section className="mb-6 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
          {requestState === "em_desenvolvimento"
            ? "A equipa Routify já começou a preparar a tua rota personalizada. Vais receber um email assim que estiver pronta."
            : requestState === "pronto"
              ? "A tua rota está pronta! A equipa Routify vai enviar-ta em breve — recebes um email assim que for entregue."
              : requestState === "pago"
                ? "Pagamento confirmado. Vamos começar a preparar a tua rota personalizada."
                : requestState === "aguarda_pagamento" || requestState === "orcamento_enviado"
                  ? "A proposta já está disponível. Assim que concluíres o pagamento começamos a preparar a tua rota."
                  : requestState === "em_analise"
                    ? "A equipa Routify está a analisar o teu pedido para preparar uma proposta de valor. Recebes uma resposta por email — normalmente em até 24h. Só pagas depois de aceitares a proposta."
                    : "Recebemos o teu pedido. A equipa Routify vai analisá-lo e responder por email — normalmente em até 24h."}
        </section>
      )}

      {requestState === "cancelado" && (
        <section className="mb-6 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground">
          Este pedido foi cancelado. Se foi engano, fala connosco e criamos um novo.
        </section>
      )}

      {isDelivered && (
        <section className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5">
          <div className="flex items-center gap-2 text-emerald-900">
            <CheckCircle2 className="h-5 w-5" />
            <div className="text-sm font-semibold">Rota entregue</div>
          </div>
          {row.deliverable_text && (
            <article className="prose prose-sm mt-4 max-w-none whitespace-pre-wrap text-emerald-950">
              {row.deliverable_text}
            </article>
          )}
          {row.app_route_link && (
            <a href={row.app_route_link} target="_blank" rel="noreferrer"
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand/90">
              <ExternalLink className="h-4 w-4" /> Ver rota na app
            </a>
          )}
          {row.pdfSignedUrl && (
            <a href={row.pdfSignedUrl} target="_blank" rel="noreferrer"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-900 hover:bg-emerald-200">
              <Download className="h-4 w-4" /> Descarregar PDF
            </a>
          )}
        </section>
      )}
    </main>
  );
}
